import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { AccountUser, Organization, listUsers, listOrganizations, inviteUser, updateUser, resetUserMFA } from '../api/client'
import { useAuth } from '../auth/AuthContext'

const cardStyle: React.CSSProperties = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 20, marginBottom: 16 }
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse' }
const thStyle: React.CSSProperties = {
  padding: '8px 12px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6b7280',
  textTransform: 'uppercase', borderBottom: '1px solid #e5e7eb', background: '#f9fafb',
}
const tdStyle: React.CSSProperties = { padding: '10px 12px', borderBottom: '1px solid #f3f4f6', fontSize: 14 }
const btnStyle: React.CSSProperties = { padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500 }
const inputSt: React.CSSProperties = { padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 14 }

const statusColors: Record<string, { bg: string; color: string }> = {
  invited: { bg: '#fef3c7', color: '#92400e' },
  active: { bg: '#d1fae5', color: '#065f46' },
  disabled: { bg: '#fee2e2', color: '#991b1b' },
}

export default function Users() {
  const { user: me } = useAuth()
  const qc = useQueryClient()
  const { data: users = [] } = useQuery<AccountUser[]>({ queryKey: ['users'], queryFn: listUsers })
  const { data: orgs = [] } = useQuery<Organization[]>({ queryKey: ['organizations'], queryFn: listOrganizations })

  const [showInvite, setShowInvite] = useState(false)
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [orgID, setOrgID] = useState<number | ''>('')
  const [isSuperUser, setIsSuperUser] = useState(false)
  const [canManageUsers, setCanManageUsers] = useState(false)
  const [canManageDevices, setCanManageDevices] = useState(true)
  const [canManagePlatforms, setCanManagePlatforms] = useState(false)
  const [inviteResult, setInviteResult] = useState<string | null>(null)

  const inviteMut = useMutation({
    mutationFn: () =>
      inviteUser({
        orgID: orgID === '' ? undefined : orgID,
        email,
        name,
        isSuperUser,
        canManageUsers,
        canManageDevices,
        canManagePlatforms,
      }),
    onSuccess: res => {
      qc.invalidateQueries({ queryKey: ['users'] })
      setEmail('')
      setName('')
      setInviteResult(res.emailWarning ? `Invited, but: ${res.emailWarning}` : `Invited ${res.user.Email}.`)
    },
  })

  const orgName = (id: number) => orgs.find(o => o.ID === id)?.Name ?? `#${id}`

  const canManage = me?.IsSuperUser || me?.CanManageUsers

  const [search, setSearch] = useState('')
  const [orgFilter, setOrgFilter] = useState<number | ''>('')
  const searchLower = search.trim().toLowerCase()
  const filteredUsers = users.filter(u => {
    if (orgFilter !== '' && u.OrgID !== orgFilter) return false
    if (!searchLower) return true
    return u.Email.toLowerCase().includes(searchLower) || u.Name.toLowerCase().includes(searchLower)
  })

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>Users</h3>
        {canManage && (
          <button style={{ ...btnStyle, background: '#2563eb', color: '#fff' }} onClick={() => setShowInvite(!showInvite)}>
            {showInvite ? 'Cancel' : '+ Invite User'}
          </button>
        )}
      </div>

      {showInvite && (
        <div style={{ ...cardStyle, borderColor: '#2563eb' }}>
          <h4 style={{ margin: '0 0 12px' }}>Invite User</h4>
          <p style={{ fontSize: 12.5, color: '#6b7280', margin: '0 0 12px' }}>
            They'll get an email with a link to set their own password — no need to make one up for them.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Email</label>
              <input style={{ ...inputSt, width: '100%', boxSizing: 'border-box' }} type="email" value={email} onChange={e => setEmail(e.target.value)} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Name</label>
              <input style={{ ...inputSt, width: '100%', boxSizing: 'border-box' }} value={name} onChange={e => setName(e.target.value)} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Organization (blank = your own)</label>
              <select style={{ ...inputSt, width: '100%', boxSizing: 'border-box' }} value={orgID} onChange={e => setOrgID(e.target.value === '' ? '' : Number(e.target.value))}>
                <option value="">— your org —</option>
                {orgs.map(o => <option key={o.ID} value={o.ID}>{o.Name}</option>)}
              </select>
            </div>
          </div>
          {me?.IsSuperUser && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 12, color: '#991b1b', fontWeight: 600 }}>
              <input type="checkbox" checked={isSuperUser} onChange={e => setIsSuperUser(e.target.checked)} /> Super User (full access to every organization)
            </label>
          )}
          <div style={{ display: 'flex', gap: 20, marginBottom: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <input type="checkbox" checked={canManageUsers} onChange={e => setCanManageUsers(e.target.checked)} /> Manage users
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <input type="checkbox" checked={canManageDevices} onChange={e => setCanManageDevices(e.target.checked)} /> Manage devices
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <input type="checkbox" checked={canManagePlatforms} onChange={e => setCanManagePlatforms(e.target.checked)} /> Manage platforms
            </label>
          </div>
          <button
            style={{ ...btnStyle, background: '#2563eb', color: '#fff' }}
            disabled={!email.trim() || inviteMut.isPending}
            onClick={() => inviteMut.mutate()}
          >
            {inviteMut.isPending ? 'Sending invite…' : 'Send Invite'}
          </button>
          {inviteResult && <p style={{ fontSize: 13, color: '#065f46', marginTop: 10 }}>{inviteResult}</p>}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <input
          style={{ flex: 1, maxWidth: 320, padding: '7px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}
          placeholder="Search by email or name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          style={{ padding: '7px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}
          value={orgFilter}
          onChange={(e) => setOrgFilter(e.target.value === '' ? '' : Number(e.target.value))}
        >
          <option value="">All organizations</option>
          {orgs.map(o => <option key={o.ID} value={o.ID}>{o.Name}</option>)}
        </select>
        {(search || orgFilter !== '') && (
          <button
            style={{ ...btnStyle, background: 'none', color: '#6b7280' }}
            onClick={() => { setSearch(''); setOrgFilter('') }}
          >
            Clear filters
          </button>
        )}
      </div>

      <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Email</th>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Organization</th>
              <th style={thStyle}>Permissions</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>MFA</th>
              {canManage && <th style={thStyle}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 && (
              <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>
                {users.length === 0 ? 'No users visible.' : 'No users match your filters.'}
              </td></tr>
            )}
            {filteredUsers.map(u => (
              <UserRow key={u.ID} u={u} orgName={orgName} canManage={!!canManage} viewerIsSuper={!!me?.IsSuperUser} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function UserRow({
  u, orgName, canManage, viewerIsSuper,
}: { u: AccountUser; orgName: (id: number) => string; canManage: boolean; viewerIsSuper: boolean }) {
  const qc = useQueryClient()
  const toggleMut = useMutation({
    mutationFn: (patch: Partial<AccountUser>) => updateUser(u.ID, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  })
  const resetMFAMut = useMutation({
    mutationFn: () => resetUserMFA(u.ID),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  })
  const sc = statusColors[u.Status] ?? statusColors.disabled

  return (
    <tr>
      <td style={tdStyle}>{u.Email}</td>
      <td style={tdStyle}>{u.Name || '—'}</td>
      <td style={tdStyle}>{u.IsSuperUser ? <em>all (super user)</em> : orgName(u.OrgID)}</td>
      <td style={{ ...tdStyle, fontSize: 12.5, color: '#6b7280' }}>
        {u.IsSuperUser
          ? 'Full access'
          : [u.CanManageUsers && 'Users', u.CanManageDevices && 'Devices', u.CanManagePlatforms && 'Platforms'].filter(Boolean).join(', ') || 'View only'}
      </td>
      <td style={tdStyle}>
        <span style={{ background: sc.bg, color: sc.color, borderRadius: 9999, padding: '2px 10px', fontSize: 12, fontWeight: 600, textTransform: 'capitalize' }}>
          {u.Status}
        </span>
      </td>
      <td style={{ ...tdStyle, fontSize: 12.5, color: '#6b7280' }}>
        {u.MFAEnabled ? `Enabled (${u.MFAMethod === 'totp' ? 'app' : 'email'})` : '—'}
      </td>
      {canManage && (
        <td style={{ ...tdStyle, display: 'flex', gap: 6 }}>
          {u.MFAEnabled && (
            <button
              style={{ ...btnStyle, background: '#f3f4f6', color: '#374151' }}
              onClick={() => { if (window.confirm(`Reset MFA for ${u.Email}? They will need to set it up again.`)) resetMFAMut.mutate() }}
              disabled={resetMFAMut.isPending}
            >
              Reset MFA
            </button>
          )}
          {!u.IsSuperUser && (
            <button
              style={{ ...btnStyle, background: u.Status === 'disabled' ? '#d1fae5' : '#fee2e2', color: u.Status === 'disabled' ? '#065f46' : '#991b1b' }}
              onClick={() => toggleMut.mutate({ Status: u.Status === 'disabled' ? 'active' : 'disabled' })}
              disabled={toggleMut.isPending || u.Status === 'invited'}
            >
              {u.Status === 'disabled' ? 'Enable' : 'Disable'}
            </button>
          )}
          {/* Only an existing super user can grant/revoke super status —
              matches the server-side gate in handleUserByID. */}
          {viewerIsSuper && (
            <button
              style={{ ...btnStyle, background: '#f3f4f6', color: '#374151' }}
              onClick={() => toggleMut.mutate({ IsSuperUser: !u.IsSuperUser })}
              disabled={toggleMut.isPending}
            >
              {u.IsSuperUser ? 'Revoke Super' : 'Make Super'}
            </button>
          )}
        </td>
      )}
    </tr>
  )
}
